<template>
	<div class="sub-resource" :class="{ 'is-locked': isLocked }">
		<Tooltip v-if="!isLocked" :heading="resourceName" customWidth="18rem">
			<template #element>
				<div class="sub-resource__content">
					<AppMainIcon
						v-if="iconName"
						class="sub-resource__icon"
						:iconName="iconName"
						:iconSize="iconSize"
					/>
					<span
						v-if="value !== undefined"
						class="sub-resource__value"
						:class="{
							'sub-resource__value--max': isMax,
							'sub-resource__value--negative': value && value < 0,
						}"
						v-html="formatNumber(animatedValueRef, true)"
					/>
					<AppNotification
						v-if="negativeIncome && !isMax && !isOverMax"
						class="sub-resource__negative-notification"
						value="!"
						:type="StatusTypes.WARNING"
					/>
					<span
						v-else-if="isMax || isOverMax"
						:class="{
							'sub-resource__max-label': isMax,
							'sub-resource__over-max-label': isOverMax,
						}"
					>
						MAX
					</span>
				</div>
			</template>
			<template #tooltip>
				<div class="sub-resource__tooltip" :class="iconName">
					<div class="sub-resource__tooltip-state">
						{{
							`${formatNumber(Number(value))} / ${formatNumber(Number(max))}`
						}}
					</div>
					<span class="sub-resource__tooltip-info">
						{{ translate(`resource.${iconName}_description`) }}
					</span>
					<div
						class="sub-resource__tooltip-warning"
						v-if="negativeIncome && !isMax && !isOverMax"
					>
						<div class="sub-resource__tooltip-warning-head">
							{{ translate("resource.insufficient_production") }}
						</div>
						<div>
							{{ translate(`resource.${iconName}_warning_1`) }}
						</div>
						<div class="sub-resource__tooltip-warning-bold">
							{{ translate(`resource.${iconName}_warning_2`) }}
						</div>
					</div>
				</div>
			</template>
		</Tooltip>
		<Tooltip v-else :heading="resourceName" customWidth="13rem">
			<template #element>
				<div class="sub-resource__lock-wrapper">
					<div class="sub-resource__lock icon-lock" />
				</div>
			</template>
			<template #tooltip>
				<span class="sub-resource__tooltip-info">
					{{
						translate("ui.available_from_level").replace(
							"{number}",
							unlockLevel.toString(),
						)
					}}
				</span>
			</template>
		</Tooltip>

		<div
			v-if="!isLocked"
			class="sub-resource__progress"
			:class="{
				'sub-resource__progress--max': isMax,
				'sub-resource__progress--negative':
					negativeIncome && !isMax && !isOverMax,
			}"
			:style="{ width: computedWidth, left: computedLeft }"
		/>
		<SubResourceDivider v-if="hasDivider" />
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import SubResourceDivider from "@/components/SVG/SubResourceDivider.vue";
import Tooltip from "@/components/Tooltip.vue";
import { NUMBER_ANIMATION_DURATION } from "@/config/constants";
import type { ResourceType } from "@/game/entities/config/resources";
import useResourcesStore from "@/store/useResourcesStore";
import { CurrencyTypesTranslation, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, shallowRef, watch } from "vue";
// @ts-expect-error - TS7016
import { linear, useTween } from "vue-femtotween";
import AppNotification from "./AppNotification.vue";

interface Props {
	iconName: ResourceType;
	iconSize?: number;
	value?: number;
	max?: number;
	progress?: number;
	isMax?: boolean;
	isOverMax?: boolean;
	isLocked?: boolean;
	hasDivider?: boolean;
	dividerShape?: [number, number, number, number];
	unlockLevel?: number;
}

const {
	iconName,
	iconSize = 20,
	value,
	max = 0,
	progress = 0,
	isMax = false,
	isOverMax = false,
	isLocked = true,
	hasDivider = false,
	dividerShape = [0, 0, 0, 0], // TODO
	unlockLevel = 1,
} = defineProps<Props>();

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const resourceName = translate(CurrencyTypesTranslation[iconName]!);

const negativeIncome = computed((): boolean => {
	const resourceIncome = resources[`${iconName}Income` as ResourceType].value;

	const resourceConstumption =
		resources[`${iconName}Consumption` as ResourceType].value;

	return resourceIncome - resourceConstumption < 0;
});

const computedWidth = computed((): string => {
	const dividerLeft = dividerShape[1] ? 0.125 : 0;
	const dividerRight = dividerShape[3] ? 0.125 : 0;
	if (isMax) return `calc(100% - ${dividerLeft + dividerRight}rem)`;
	return `calc(${Math.min(Math.max((progress || 0) * 100, 0), 100)}% - ${dividerLeft + dividerRight}rem)`;
});

const computedLeft = computed((): string => {
	const dividerLeft = dividerShape[3] ? 0.125 : 0;
	return `${dividerLeft}rem`;
});

const valueRef = shallowRef<number>(value ?? 0);
const animatedValueRef = useTween(valueRef, {
	easing: linear,
	time: NUMBER_ANIMATION_DURATION,
	precision: 0,
});

watch(
	(): number | undefined => value,
	(): void => {
		animatedValueRef.stop();
		animatedValueRef.value = valueRef.value ?? 0;
		valueRef.value = value ?? 0;
	},
);
</script>

<style lang="scss" scoped>
.sub-resource {
	display: flex;
	position: relative;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	height: 1.563rem;

	&__content {
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		gap: 0.25rem;
		width: 100%;

		.sub-resource__icon {
			position: relative;
			&::before {
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				z-index: -1;
				filter: blur(0.375rem);
				border-radius: 50%;
				background: rgba(255, 255, 255, 0.3);
				width: 100%;
				height: 100%;
				content: "";
			}
		}

		.sub-resource__value {
			padding-bottom: 0.125rem;
			color: #fff3dd;
			font-size: 0.75rem;
			line-height: 0.75rem;

			&--max {
				color: #f4d278;
			}

			&--negative {
				color: #ff3d3d;
			}
		}

		.sub-resource__max-label,
		.sub-resource__over-max-label {
			position: absolute;
			top: -0.125rem;
			right: 0.125rem;
			font-size: 0.375rem;
		}
		.sub-resource__negative-notification {
			position: absolute;
			top: 0;
			right: 0;
			transform: scale(0.7);
		}

		.sub-resource__max-label {
			color: #f4d278;
		}

		.sub-resource__over-max-label {
			color: #ff3d3d;
		}
	}

	&__progress {
		position: absolute;
		bottom: 0;
		align-self: flex-start;
		background: #dcdcdc;
		width: 100%;
		height: 0.125rem;

		&--max {
			background: #f4d278;
		}
		&--negative {
			background: #b84034;
		}
	}

	&__lock {
		position: relative;
		width: 1.25rem;
		height: 1.25rem;
		@include clickable-element;
		&-wrapper {
			width: 100%;
			@include flex-row-center;
		}

		&::before {
			display: block;
			position: absolute;
			top: 0;
			left: 0;
			z-index: -1;
			filter: blur(0.375rem);
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.2);
			width: 100%;
			height: 100%;
			content: "";
		}
	}

	.sub-resource-divider {
		position: absolute;
		right: -0.125rem;
		bottom: 0;
		z-index: -1;
		height: 100%;
	}

	&__tooltip {
		&-state {
			@include flex-row-center;
			position: relative;
			margin: 0 auto;
			margin-bottom: 0.25rem;
			box-shadow: inset 0 0 0 0.063rem rgba(255, 255, 255, 0.1);
			background-color: #2e2d39;
			padding: 0.25rem 0.5rem;
			width: fit-content;
			color: #fff3dd;
			font-size: 0.813rem;
			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1 !important;
				// NOTE: Multiply blending is better for light colors
				mix-blend-mode: multiply !important;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 60%;
				width: 100%;
				height: 100%;
				content: "";
			}
		}
		&-warning {
			@include flex-col-center;
			position: relative;
			margin-top: 0.25rem;
			box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
			border: 0.063rem solid #cf0032;
			background-color: #401622;
			padding: 0.5rem;
			text-align: center;
			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1 !important;
				// NOTE: Multiply blending is better for light colors
				mix-blend-mode: multiply !important;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 60%;
				width: 100%;
				height: 100%;
				content: "";
			}
			& > div {
				color: #cf0032;
				font-size: 0.813rem;
			}
			&-head {
				font-weight: bold;
				font-size: 0.906rem;
				text-transform: uppercase;
			}
			&-bold {
				font-weight: bold;
			}
		}
		&-info {
			@include flex-row-center;
			color: #372615;
			font-size: 0.813rem;
			text-align: center;
		}
	}
	:deep(.v-popper) {
		@include flex-row-center;
		width: 100%;
	}
}
</style>
